import {type Match} from '@wandb/weave/common/types/base';
import {getCookie} from '@wandb/weave/common/util/cookie';
import React, {useEffect} from 'react';

import {startDatadogRumView} from '../util/datadog/datadogRum';
import {trackPage} from '../util/navigation';
import {type AppRouteData} from './types';

const HOST_SESSION_ID_COOKIE = `host_session_id`;

export const getRouteTab = (
  match: Match,
  additionalProps: AppRouteData['additionalProps']
) => {
  if (match?.params?.tab) {
    return match.params.tab;
  }
  if ('tab' in additionalProps) {
    return additionalProps.tab;
  }
  return undefined;
};

/**
 * A lightweight wrapper component that handles analytics and datadog RUM tracking at route level.
 * This should only wrap the component that gets rendered when the path matches because we only want
 * to trigger tracking ONCE.
 */
export const TrackRouteComponent = ({
  children,
  routeName,
  tabParam,
}: {
  children: React.ReactNode;
  routeName: string;
  /** @deprecated - the goal is to remove this parameter in favor of using just routeName in the future with validation when envIsDev  */
  tabParam?: string;
}) => {
  useEffect(() => {
    const tabSuffix = tabParam ? `:${tabParam}` : '';
    const routeNameWithTab = routeName + tabSuffix;

    trackPage(
      {
        routeName,
        tab: tabParam,
        routeAndTab: routeNameWithTab,
        isNew: true,
      },
      {
        context: {
          hostSessionID: getCookie(HOST_SESSION_ID_COOKIE),
        },
      }
    );

    startDatadogRumView(routeNameWithTab);

    // By defining a cleanup that clears the route name, we ensure
    // that the profiler is updated to not have a route name when the
    // user leaves the page.
    // There are few pages that don't get wrapped in this comp, so this is
    // mostly for future-proofing.
    return () => {
      startDatadogRumView(routeNameWithTab);
    };
  }, [routeName, tabParam]);

  return <>{children}</>;
};
